import React, { useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';

// Import the prediction styles for consistent coloring
const predictionStyles = {
  colors: {
    success: 'rgba(76, 175, 80, 0.9)',  // Green
    successSolid: 'rgba(76, 175, 80, 1)',
    miss: 'rgba(244, 67, 54, 0.9)',     // Red  
    missSolid: 'rgba(244, 67, 54, 1)',
    pending: 'rgba(255, 152, 0, 0.9)',  // Orange
    pendingSolid: 'rgba(255, 152, 0, 1)',
    updated: 'rgba(139, 92, 246, 0.9)',  // Purple
    updatedSolid: 'rgba(139, 92, 246, 1)',
    // Bet prediction colors (matching desktop)
    betWinnerSolid: 'rgba(34, 197, 94, 1)', // Green for bet winners
    betLoserSolid: 'rgba(239, 68, 68, 1)',  // Red for bet losers
  }
};

// Helper function to format prices for chart labels (compact but readable)
const formatChartPrice = (price: number): string => {
  // Handle negative values
  if (price < 0) {
    return `-${formatChartPrice(Math.abs(price))}`;
  }
  
  if (price >= 1000000) {
    // For millions: $1M, $1.1M, $13M
    const millions = price / 1000000;
    return millions >= 10 ? `$${millions.toFixed(0)}M` : `$${millions.toFixed(1)}M`;
  } else if (price >= 1000) {
    // For thousands: $100k, $112k
    return `$${(price / 1000).toFixed(0)}k`;
  } else {
    // For smaller numbers: $500, $0, etc.
    return `$${price.toFixed(0)}`;
  }
};

// Helper function to get status-based color
const getStatusColor = (prediction: Prediction, solid = false) => {
  if (prediction.success_status === 'success') {
    return solid ? predictionStyles.colors.successSolid : predictionStyles.colors.success;
  } else if (prediction.success_status === 'miss') {
    return solid ? predictionStyles.colors.missSolid : predictionStyles.colors.miss;
  } else if (prediction.success_status === 'updated') {
    return solid ? predictionStyles.colors.updatedSolid : predictionStyles.colors.updated;
  } else {
    return solid ? predictionStyles.colors.pendingSolid : predictionStyles.colors.pending;
  }
};

// Helper function to get status text
const getStatusText = (prediction: Prediction): string => {
  switch (prediction.success_status) {
    case 'success': return '✓ HIT';
    case 'miss': return '✗ MISS';
    case 'updated': return '🔄 UPDATED';
    default: return '⏳ PENDING';
  }
};

// Helper function for collision detection - prevents overlapping labels
const resolveCollisions = (labels: Array<{x: number, y: number, height: number, text: string, id: string, color: string, type?: string, participant?: any}>) => {
  const resolvedLabels = [...labels];
  const minSpacing = 3; // Minimum spacing between labels
  
  // Sort by Y position
  resolvedLabels.sort((a, b) => a.y - b.y);
  
  // Check for overlaps and adjust positions
  for (let i = 1; i < resolvedLabels.length; i++) {
    const current = resolvedLabels[i];
    const previous = resolvedLabels[i - 1];
    
    const previousBottom = previous.y + (previous.height / 2);
    const currentTop = current.y - (current.height / 2);
    
    if (currentTop < previousBottom + minSpacing) {
      // Collision detected - move current label down
      current.y = previousBottom + minSpacing + (current.height / 2);
    }
  }
  
  return resolvedLabels;
};

// Helper function to add text labels with black background (like main chart)
const addLabelWithBackground = (
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>,
  x: number,
  y: number,
  text: string,
  color: string,
  textAnchor: 'start' | 'middle' | 'end' = 'end',
  onClick?: () => void,
  fontSize: string = '13px',
  fontWeight: string = '600'
) => {
  // Create group for text and background
  const labelGroup = chartArea.append("g");
  
  // Add the text element (temporarily to measure)
  const textElement = labelGroup.append("text")
    .attr("x", x)
    .attr("y", y)
    .style("fill", color)
    .style("font-size", fontSize)
    .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .style("font-weight", fontWeight)
    .style("opacity", 0.9)
    .style("text-anchor", textAnchor)
    .style("cursor", "pointer")
    .text(text);
  
  // Get text dimensions for background
  const bbox = (textElement.node() as SVGTextElement).getBBox();
  const padding = 4;
  
  // Insert background rectangle before text
  labelGroup.insert("rect", ":first-child")
    .attr("x", bbox.x - padding)
    .attr("y", bbox.y - padding)
    .attr("width", bbox.width + 2 * padding)
    .attr("height", bbox.height + 2 * padding)
    .attr("rx", 3)
    .attr("ry", 3)
    .style("fill", "rgba(0, 0, 0, 0.8)")
    .style("stroke", "rgba(255, 255, 255, 0.2)")
    .style("stroke-width", 1);
  
  // Add click handler if provided
  if (onClick) {
    labelGroup
      .style("cursor", "pointer")
      .on("click", function(event) {
        event.stopPropagation();
        onClick();
      });
  }
  
  return labelGroup;
};

// Helper function to find crossing point (borrowed from desktop version)
const findCrossingPoint = (
  chartData: Array<{ time: Date; value: number }>,
  prediction: Prediction
): { time: Date; value: number } | null => {
  if (!chartData.length) return null;

  let targetPrice: number | null = null;
  
  if (prediction.prediction_type === 'target') {
    const priceValue = prediction.price_target ?? prediction.price_min ?? prediction.price_prediction;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'top') {
    // TOP predictions use price_max or fallback to price_target
    const priceValue = prediction.price_max ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'bottom') {
    // BOTTOM predictions use price_min or fallback to price_target  
    const priceValue = prediction.price_min ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'pricetimerange') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null;
  } else if (prediction.prediction_type === 'range') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null; // Use min as the threshold that was reached
  } else if (prediction.prediction_type === 'bet') {
    // BET predictions use price_prediction, price_target, or price_min for the bet amount
    const priceValue = prediction.price_prediction ?? prediction.price_target ?? prediction.price_min;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  }

  if (targetPrice === null || targetPrice === undefined) return null;

  // Sort data chronologically
  const sortedData = [...chartData].sort((a, b) => a.time.getTime() - b.time.getTime());
  
  // Get the valid time range for this prediction
  const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
  
  // Find where price crosses the target WITHIN the valid time range
  for (let i = 1; i < sortedData.length; i++) {
    const prev = sortedData[i-1];
    const curr = sortedData[i];
    
    // Skip if current point is outside the prediction's time range
    if (startDate && curr.time < startDate) continue;
    
    // Check if price crosses target between these points
    if (prev.value < targetPrice && curr.value >= targetPrice) {
      return curr;
    }
  }
  
  return null;
};

interface MobilePriceChartProps {
  predictions: Prediction[];
  activePredictions: number[];
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
}

export const MobilePriceChart: React.FC<MobilePriceChartProps> = ({
  predictions,
  activePredictions,
  chartData,
  isDarkMode,
  theme
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();
  const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);

  // Reset zoom to default view
  const resetZoom = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(750)
      .call(zoomRef.current.transform, d3.zoomIdentity);
    
    transformRef.current = d3.zoomIdentity;
  }, []);

  // Redraw chart function that can be called by zoom
  const redrawChart = useCallback((transform: d3.ZoomTransform = d3.zoomIdentity) => {
    if (!svgRef.current || chartData.length === 0) return;


    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Mobile-optimized dimensions with moderate space for prediction labels
    const margin = { top: 15, right: 40, bottom: 25, left: 45 }; // Reduced right margin from 80px to 40px
    const width = svgRef.current.clientWidth - margin.left - margin.right;
    const height = svgRef.current.clientHeight - margin.top - margin.bottom;

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add clipping path to hide elements outside chart area
    const clipId = `chart-clip-${Math.random().toString(36).substr(2, 9)}`;
    svg.append("defs").append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height);

    const chartArea = g.append("g")
      .attr("clip-path", `url(#${clipId})`);

    // Parse and sort data
    const parseTime = d3.timeParse("%Y-%m-%d");
    const processedData = chartData
      .map(d => ({
        ...d,
        time: parseTime(d.time) || new Date(d.time),
        value: +d.value
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    // Debug: Log invalid dates
    processedData.forEach(d => {
      if (isNaN(d.time.getTime())) {
        console.warn('Invalid date:', d.time, d);
      }
    });

    if (processedData.length === 0) return;

    // ULTRA-COMPACT VIEWPORT: Get 0 level super close to X-axis
    const prices = processedData.map(d => d.value);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    
    // Get active prediction prices to adjust Y-axis if needed
    const activePredictionPrices = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .map(p => {
        if (p!.prediction_type === 'target') {
          // Target predictions can have price in price_target, price_min, or price_prediction
          const priceValue = p!.price_target ?? p!.price_min ?? p!.price_prediction;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'range') {
          // For range predictions, include both min and max prices
          const minPrice = typeof p!.price_min === 'string' ? parseFloat(p!.price_min) : (p!.price_min ?? null);
          const maxPrice = typeof p!.price_max === 'string' ? parseFloat(p!.price_max) : (p!.price_max ?? null);
          return [minPrice, maxPrice].filter(price => price !== null && isFinite(price));
        } else if (p!.prediction_type === 'pricetimerange') {
          // ✅ FIX: Handle pricetimerange predictions correctly - they use price_min
          const priceValue = p!.price_min ?? p!.price_target;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'top') {
          // TOP predictions use price_max or fallback to price_target/price_min
          const priceValue = p!.price_max ?? p!.price_target ?? p!.price_min;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'bottom') {
          // BOTTOM predictions use price_min or fallback to price_target
          const priceValue = p!.price_min ?? p!.price_target ?? p!.price_prediction;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'bet') {
          // BET predictions use price_prediction, price_target, or price_min for the bet amount
          const priceValue = p!.price_prediction ?? p!.price_target ?? p!.price_min;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        }
        return null;
      })
      .flat() // Flatten array to handle range predictions that return arrays
      .filter(price => price !== null && price !== undefined && isFinite(price)) as number[];
    
    
    // Include prediction prices in the range calculation
    const allPrices = activePredictionPrices.length > 0 ? [...prices, ...activePredictionPrices] : prices;
    const finalMinPrice = Math.min(...allPrices);
    const finalMaxPrice = Math.max(...allPrices);
    const finalPriceRange = finalMaxPrice - finalMinPrice;
    
    // Ultra-minimal Y range - get 0 super close to X-axis, but expand for predictions
    const padding = Math.max(finalPriceRange * 0.15, 5000); // Increased padding from 0.1 to 0.15 and min from 1000 to 5000
    let yMin = finalMinPrice - padding; // Allow negative values for predictions like Peter Zeihan's -$1000
    let yMax = Math.max(finalMaxPrice + padding, 200000); // Expand ceiling if predictions require it
    
    // ✅ FIX: Prevent unrealistic negative values in Y-axis (limit to -$1000)
    if (yMin < -1000) {
      yMin = -1000; // Hard limit at -$1000 (don't go lower than this)
    }
    
    // ✅ FIX: Dynamic Y-axis expansion for high-value predictions (like Samson Mow's $1M, Chamath's $1.14M)
    if (activePredictionPrices.some(price => price >= 1000000)) {
      // For 1M+ predictions, expand to 1.5M to give more space for very high predictions
      yMax = Math.max(yMax, 1500000);
    } else if (activePredictionPrices.some(price => price > 300000)) {
      yMax = Math.max(yMax, 400000);
    }
    

    // Debug: Log Peter Zeihan's prediction specifically if it's active
    const peterZeihanPrediction = predictions.find(p => p.influencer?.name?.toLowerCase().includes('zeihan'));
    if (peterZeihanPrediction && activePredictions.includes(peterZeihanPrediction.id)) {
      console.log('Peter Zeihan prediction debug:', {
        id: peterZeihanPrediction.id,
        price_prediction: peterZeihanPrediction.price_prediction,
        price_min: peterZeihanPrediction.price_min,
        includedInActivePrices: activePredictionPrices.includes(-1000),
        yMin: yMin,
        yMax: yMax
      });
    }

    // Create smart time window: Always start from 2019
    let timeExtent = d3.extent(processedData, d => d.time) as [Date, Date];
    
    // Get active prediction dates
    const allActiveIds = [...activePredictions];
    
    const activePredictionDates: Date[] = allActiveIds
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .flatMap(p => {
        const dates: Date[] = [];
        
        // For pricetimerange predictions, use start_date and end_date
        if (p!.prediction_type === 'pricetimerange' && p!.start_date && p!.end_date) {
          dates.push(new Date(p!.start_date));
          dates.push(new Date(p!.end_date));
        }
        // For TOP and BOTTOM predictions, use start_date and extend beyond current chart
        else if ((p!.prediction_type === 'top' || p!.prediction_type === 'bottom') && p!.start_date) {
          dates.push(new Date(p!.start_date));
          // Extend the time window beyond current date for TOP/BOTTOM predictions
          const futureDate = new Date();
          futureDate.setFullYear(futureDate.getFullYear() + 2); // Extend 2 years into future
          dates.push(futureDate);
        }
        // For bet predictions, use start_date and end_date
        else if (p!.prediction_type === 'bet') {
          if (p!.start_date) dates.push(new Date(p!.start_date));
          if (p!.end_date) dates.push(new Date(p!.end_date));
        }
        // For other predictions, use target_time or end_date
        else if (p!.target_time) {
          dates.push(new Date(p!.target_time));
        }
        else if (p!.end_date) {
          dates.push(new Date(p!.end_date));
        }
        
        return dates.filter(date => !isNaN(date.getTime()));
      });
    
    if (activePredictionDates.length > 0) {
      // Smart time window: Show from 2015 + prediction period with extra padding for labels
      const latestDataDate = timeExtent[1]; // Most recent price data
      const latestPrediction = new Date(Math.max(...activePredictionDates.map(d => d.getTime())));
      
      // Start earlier for better visibility of left-side labels - allow going back to 2015
      const startOf2015 = new Date('2015-01-01'); // Start from 2015 instead of 2018
      const smartStartDate = new Date(Math.max(startOf2015.getTime(), timeExtent[0].getTime()));
      
      // End at prediction date with more padding for labels
      const paddingMonths = 12; // Increased from 6 to 12 months
      const smartEndDate = new Date(latestPrediction.getTime() + (paddingMonths * 30 * 24 * 60 * 60 * 1000));
      
      timeExtent = [smartStartDate, smartEndDate];
    } else {
      // No active predictions: show from 2015 onwards, but default view focused on recent years
      const latestDataDate = timeExtent[1];
      const startOf2015 = new Date('2015-01-01'); // Start from 2015 for more historical data
      const smartStartDate = new Date(Math.max(startOf2015.getTime(), timeExtent[0].getTime()));
      
      // Add future padding to shift current price toward center
      const futureMonths = 18; // Increased from 12 to 18 months for more space
      const futurePadding = futureMonths * 30 * 24 * 60 * 60 * 1000;
      const smartEndDate = new Date(latestDataDate.getTime() + futurePadding);
      
      timeExtent = [smartStartDate, smartEndDate];
    }

    const xScale = d3.scaleTime()
      .domain(timeExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    // Apply zoom transform to scales
    const zoomedXScale = transform.rescaleX(xScale);
    let zoomedYScale = transform.rescaleY(yScale);
    
    // ✅ CRITICAL FIX: Constrain zoomed Y-axis to never go below -$1000
    const zoomedYDomain = zoomedYScale.domain();
    if (zoomedYDomain[0] < -1000) {
      // If the zoomed domain tries to go below -$1000, clamp it
      const clampedYMin = -1000;
      const clampedYMax = zoomedYDomain[1];
      zoomedYScale = d3.scaleLinear()
        .domain([clampedYMin, clampedYMax])
        .range([height, 0]);
    }

    // Add X axis - always start display from 2019
    const displayTimeExtent = timeExtent;
    const yearSpan = displayTimeExtent[1].getFullYear() - displayTimeExtent[0].getFullYear();
    
    let tickFormat: (date: Date) => string;
    let tickCount: number;
    
    if (yearSpan > 3) {
      tickFormat = d3.timeFormat("'%y");
      tickCount = Math.min(yearSpan, 6);
    } else if (yearSpan > 1) {
      tickFormat = d3.timeFormat("%Y");
      tickCount = yearSpan + 1;
    } else {
      tickFormat = d3.timeFormat("%b");
      tickCount = 6;
    }

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(zoomedXScale)
        .tickFormat(tickFormat as any)
        .ticks(tickCount))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px")
      .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif");

    // Add Y axis
    g.append("g")
      .call(d3.axisLeft(zoomedYScale)
        .tickFormat(d => `$${(d as number / 1000).toFixed(0)}k`)
        .ticks(5))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px")
      .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif");

    // Style axis lines
    g.selectAll(".domain, .tick line")
      .style("stroke", theme.border);

    // Add horizontal grid lines (Y-axis)
    chartArea.selectAll(".y-grid-line")
      .data(zoomedYScale.ticks(6))
      .enter().append("line")
      .attr("class", "y-grid-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => zoomedYScale(d))
      .attr("y2", d => zoomedYScale(d))
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.4)
      .style("stroke-width", 1);

    // Add vertical grid lines (X-axis)
    chartArea.selectAll(".x-grid-line")
      .data(zoomedXScale.ticks(5))
      .enter().append("line")
      .attr("class", "x-grid-line")
      .attr("x1", d => zoomedXScale(d))
      .attr("x2", d => zoomedXScale(d))
      .attr("y1", 0)
      .attr("y2", height)
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.25)
      .style("stroke-width", 1);

    // Create line generator with zoomed scales
    const line = d3.line<typeof processedData[0]>()
      .x(d => zoomedXScale(d.time))
      .y(d => zoomedYScale(d.value))
      .curve(d3.curveMonotoneX);

    // Add the main price line (clipped)
    chartArea.append("path")
      .datum(processedData)
      .attr("fill", "none")
      .attr("stroke", theme.primary)
      .attr("stroke-width", 2.5)
      .attr("d", line);

    // Add watermark to the chart
    svg.append('g')
      .attr('class', 'chart-watermark')
      .attr('transform', `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`)
      .style('pointer-events', 'none') // Make sure it doesn't capture touch events
      .call(g => {
        // Add "proofofprice.com" watermark text
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('font-size', '20px')
          .attr('font-weight', 'bold')
          .attr('font-family', 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif')
          .attr('fill', theme.text)
          .attr('opacity', 0.06)
          .attr('transform', 'rotate(-15)')
          .text('proofofprice.com');
      });

    // Add current price indicator (clipped)
    const latestPoint = processedData[processedData.length - 1];
    if (latestPoint) {
      const currentX = zoomedXScale(latestPoint.time);
      const currentY = zoomedYScale(latestPoint.value);

      // Only show if within visible area
      if (currentX >= 0 && currentX <= width) {
        // Determine price change direction and color
        const previousPoint = processedData[processedData.length - 2];
        let priceChangeColor = theme.primary;
        let priceChangeSymbol = '';
        
        if (previousPoint) {
          const priceDiff = latestPoint.value - previousPoint.value;
          if (priceDiff > 0) {
            priceChangeColor = '#4CAF50'; // Green for up
            priceChangeSymbol = '↗';
          } else if (priceDiff < 0) {
            priceChangeColor = '#F44336'; // Red for down
            priceChangeSymbol = '↘';
          }
        }

        // Add subtle pulse animation ring
        chartArea.append("circle")
          .attr("cx", currentX)
          .attr("cy", currentY)
          .attr("r", 6)
          .attr("fill", "none")
          .attr("stroke", priceChangeColor)
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.6)
          .transition()
          .duration(2000)
          .ease(d3.easeLinear)
          .attr("r", 12)
          .attr("stroke-opacity", 0)
          .on("end", function() {
            // Restart the pulse animation
            d3.select(this)
              .attr("r", 6)
              .attr("stroke-opacity", 0.6)
              .transition()
              .duration(2000)
              .ease(d3.easeLinear)
              .attr("r", 12)
              .attr("stroke-opacity", 0);
          });

        // Current price dot with color coding
        chartArea.append("circle")
          .attr("cx", currentX)
          .attr("cy", currentY)
          .attr("r", 4)
          .attr("fill", priceChangeColor)
          .attr("stroke", theme.surface)
          .attr("stroke-width", 2);

        // Current price label with directional arrow (on left side)
        addLabelWithBackground(
          chartArea,
          currentX - 8,
          currentY - 8,
          `${priceChangeSymbol} $${Math.round(latestPoint.value).toLocaleString()}`,
          priceChangeColor,
          'end',
          undefined,
          '14px',
          '700'
        );
      }
    }

    // SIMPLE PREDICTION RENDERING: Show only active predictions
    activePredictions.forEach(predictionId => {
      const prediction = predictions.find(p => p.id === predictionId);
      if (!prediction) return;

      const predictionType = prediction.prediction_type || 'target';
      
      if (predictionType === 'target') {
        // TARGET PREDICTIONS: Dots with status colors
        let predictionPrice: number | null = null;
        const priceValue = prediction.price_target ?? prediction.price_min ?? prediction.price_prediction;
        predictionPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        
        // Debug logging for Peter Zeihan's prediction
        if (prediction.influencer?.name?.toLowerCase().includes('zeihan')) {
          console.log('Zeihan prediction price debug:', {
            id: prediction.id,
            priceValue: priceValue,
            predictionPrice: predictionPrice,
            isFinite: predictionPrice !== null ? isFinite(predictionPrice) : false,
            yScale: predictionPrice !== null ? yScale(predictionPrice) : 'N/A',
            target_time: prediction.target_time
          });
        }
        
        const parseTime = d3.timeParse('%Y-%m-%d');
        let predictionDate: Date | null = null;
        if (prediction.target_time) {
          predictionDate = parseTime(prediction.target_time) || new Date(prediction.target_time);
        }
        if (!predictionDate || isNaN(predictionDate.getTime())) {
          predictionDate = new Date();
        }
        
        if (predictionPrice !== null && predictionPrice !== undefined && isFinite(predictionPrice) && predictionDate) {
          const predX = zoomedXScale(predictionDate);
          const predY = zoomedYScale(predictionPrice);
          
          console.log('Prediction positioning debug:', {
            id: predictionId,
            price: predictionPrice,
            x: predX,
            y: predY,
            expertName: prediction.influencer?.name
          });
          
          // Status-based prediction dot
          chartArea.append("circle")
            .attr("cx", predX)
            .attr("cy", predY)
            .attr("r", 4)
            .attr("fill", getStatusColor(prediction, true))
            .attr("stroke", theme.surface)
            .attr("stroke-width", 2)
            .style("opacity", 0.9)
            .style("cursor", "pointer")
            .on("click", function(event) {
              event.stopPropagation();
              resetZoom();
            });
          
          // Status-based prediction label - POSITIONED TO THE LEFT
          addLabelWithBackground(
            chartArea,
            predX - 8,
            predY - 8,
            formatChartPrice(predictionPrice),
            getStatusColor(prediction, true),
            'end',
            resetZoom
          );
          
          // Add status indicator below the price
          const statusText = getStatusText(prediction);
          addLabelWithBackground(
            chartArea,
            predX - 8,
            predY + 10,
            statusText,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '10px',
            '500'
          );
        }
      }
      
      else if (predictionType === 'range') {
        // RANGE PREDICTIONS: Rectangular boxes with status colors
        const startDate = new Date(prediction.start_date || new Date());
        const endDate = new Date(prediction.end_date || prediction.target_time || new Date());
        // ✅ FIX: Convert price strings to numbers and handle zero values
        const priceMin = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? 0);
        const priceMax = typeof prediction.price_max === 'string' 
          ? parseFloat(prediction.price_max) 
          : (prediction.price_max ?? 0);
        
        if (priceMin !== null && priceMax !== null && isFinite(priceMin) && isFinite(priceMax) && priceMax > priceMin) {
          const x1 = zoomedXScale(startDate);
          const x2 = zoomedXScale(endDate);
          const y1 = zoomedYScale(priceMax); // Top of box
          const y2 = zoomedYScale(priceMin); // Bottom of box
          
          
          // Status-based transparent rectangle
          chartArea.append("rect")
            .attr("x", Math.min(x1, x2))
            .attr("y", y1)
            .attr("width", Math.abs(x2 - x1))
            .attr("height", Math.abs(y2 - y1))
            .attr("fill", getStatusColor(prediction))
            .attr("fill-opacity", 0.1)
            .attr("stroke", getStatusColor(prediction, true))
            .attr("stroke-width", 1)
            .attr("stroke-opacity", 0.6)
            .attr("rx", 2) // Rounded corners
            .style("cursor", "pointer")
            .on("click", function(event) {
              event.stopPropagation();
              resetZoom();
            });
          
          // Status-based label - POSITIONED TO THE LEFT OF BOX
          const leftX = Math.min(x1, x2);
          const centerY = (y1 + y2) / 2;
          
          addLabelWithBackground(
            chartArea,
            leftX - 8,
            centerY - 8,
            `${formatChartPrice(priceMin)}-${formatChartPrice(priceMax)}`,
            getStatusColor(prediction, true),
            'end',
            resetZoom
          );
          
          // Add status indicator for range predictions
          const statusText = getStatusText(prediction);
          addLabelWithBackground(
            chartArea,
            leftX - 8,
            centerY + 8,
            statusText,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '10px',
            '500'
          );
        }
      }
      
      else if (predictionType === 'pricetimerange') {
        // PRICE-TIME RANGE: Horizontal dashed line with status colors
        console.log('Price-time range debug:', {
          start_date: prediction.start_date,
          end_date: prediction.end_date,
          target_time: prediction.target_time,
          price_min: prediction.price_min,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        const endDate = prediction.end_date ? new Date(prediction.end_date) : null;
        // ✅ FIX: Convert price_min string to number and handle zero values
        const targetPrice = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? 0);
        
        if (!startDate || !endDate || targetPrice === null || targetPrice === undefined || !isFinite(targetPrice)) {
          console.warn(`⚠️ Invalid pricetimerange data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error(`❌ Invalid dates for prediction ${predictionId}`);
          return;
        }

        const x1 = zoomedXScale(startDate);
        const x2 = zoomedXScale(endDate);
        const y = zoomedYScale(targetPrice);
        
        
        // Status-based horizontal dashed line
        chartArea.append("line")
          .attr("x1", Math.min(x1, x2))
          .attr("x2", Math.max(x1, x2))
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "4,3")
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED TO THE LEFT OF LINE
        addLabelWithBackground(
          chartArea,
          Math.min(x1, x2) - 8,
          y - 8,
          formatChartPrice(targetPrice),
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for pricetimerange predictions
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          Math.min(x1, x2) - 8,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'top') {
        // TOP PREDICTIONS: Solid horizontal lines starting from start_date
        console.log('Top prediction debug:', {
          start_date: prediction.start_date,
          price_max: prediction.price_max,
          price_target: prediction.price_target,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        // For TOP predictions, use price_max or fallback to price_target
        const topPrice = typeof prediction.price_max === 'string' 
          ? parseFloat(prediction.price_max) 
          : (prediction.price_max ?? prediction.price_target ?? 0);
        
        if (!startDate || topPrice === null || topPrice === undefined || !isFinite(topPrice)) {
          console.warn(`⚠️ Invalid TOP data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime())) {
          console.error(`❌ Invalid start date for TOP prediction ${predictionId}`);
          return;
        }

        // Line goes from start_date to the end of the visible chart
        const [xMin, xMax] = zoomedXScale.domain();
        const x1 = zoomedXScale(startDate);
        const x2 = zoomedXScale(xMax); // End of visible chart
        const y = zoomedYScale(topPrice);
        
        
        // Status-based solid horizontal line
        chartArea.append("line")
          .attr("x1", Math.max(x1, zoomedXScale(xMin))) // Don't start before visible area
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED INSIDE THE CHART (not at the edge)
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y - 8,
          `TOP ${formatChartPrice(topPrice)}`,
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for TOP predictions
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'bottom') {
        // BOTTOM PREDICTIONS: Solid horizontal lines starting from start_date
        console.log('Bottom prediction debug:', {
          start_date: prediction.start_date,
          price_min: prediction.price_min,
          price_target: prediction.price_target,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        // For BOTTOM predictions, use price_min or fallback to price_target
        const bottomPrice = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? prediction.price_target ?? 0);
        
        if (!startDate || bottomPrice === null || bottomPrice === undefined || !isFinite(bottomPrice)) {
          console.warn(`⚠️ Invalid BOTTOM data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime())) {
          console.error(`❌ Invalid start date for BOTTOM prediction ${predictionId}`);
          return;
        }

        // Line goes from start_date to the end of the visible chart
        const [xMin, xMax] = zoomedXScale.domain();
        const x1 = zoomedXScale(startDate);
        const x2 = zoomedXScale(xMax); // End of visible chart
        const y = zoomedYScale(bottomPrice);
        
        
        // Status-based solid horizontal line
        chartArea.append("line")
          .attr("x1", Math.max(x1, zoomedXScale(xMin))) // Don't start before visible area
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED INSIDE THE CHART (not at the edge)
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y - 8,
          `BOTTOM ${formatChartPrice(bottomPrice)}`,
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for BOTTOM predictions (positioned below the line)
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'bet') {
        // BET PREDICTIONS: Dice icon with participants and price target (no tweet cards on mobile)
        console.log('Bet prediction debug:', {
          id: prediction.id,
          influencer: prediction.influencer?.name,
          price_prediction: prediction.price_prediction,
          start_date: prediction.start_date,
          end_date: prediction.end_date,
          status: prediction.success_status
        });

        const betAmount = prediction.price_prediction || prediction.price_target || prediction.price_min;
        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        const endDate = prediction.end_date ? new Date(prediction.end_date) : null;
        
        if (!betAmount || !endDate || isNaN(endDate.getTime())) {
          console.warn(`⚠️ Invalid BET data for prediction ${predictionId}`);
          return;
        }

        const betPrice = typeof betAmount === 'string' ? parseFloat(betAmount) : betAmount;
        if (!isFinite(betPrice)) {
          console.warn(`⚠️ Invalid bet price for prediction ${predictionId}:`, betAmount);
          return;
        }

        // Find ALL participants in this bet wager (same price + date)
        const betParticipants = activePredictions
          .map(id => predictions.find(p => p.id === id))
          .filter(p => p && 
            p.prediction_type === 'bet' && 
            (p.price_prediction || p.price_target || p.price_min) === betAmount &&
            p.end_date === prediction.end_date
          );

        // Only render once per bet wager (for the first participant found)
        const isFirstParticipant = betParticipants[0]?.id === predictionId;
        if (!isFirstParticipant) {
          return; // Skip rendering for other participants of the same bet
        }

        console.log('🎲 Rendering bet wager with participants:', betParticipants.map(p => p?.influencer?.name));

        const x1 = startDate ? zoomedXScale(startDate) : zoomedXScale(endDate) - 50;
        const x2 = zoomedXScale(endDate);
        const y = zoomedYScale(betPrice);
        
        // BET LINE: Orange/yellow line representing the wager timeframe
        chartArea.append("line")
          .attr("x1", x1)
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", "#F7931A") // Bitcoin orange
          .attr("stroke-width", 3)
          .attr("stroke-opacity", 0.8)
          .attr("stroke-linecap", "round")
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });

        // DICE MARKER at start of bet
        const diceGroup = chartArea.append("g").attr("class", "bet-dice-marker");
        
        diceGroup.append("circle")
          .attr("cx", x1)
          .attr("cy", y)
          .attr("r", 8)
          .attr("fill", getStatusColor(prediction, true))
          .attr("stroke", theme.surface)
          .attr("stroke-width", 2)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
          
        diceGroup.append("text")
          .attr("x", x1)
          .attr("y", y + 3)
          .attr("text-anchor", "middle")
          .attr("font-size", "10px")
          .attr("fill", theme.surface)
          .text("🎲");

        // PARTICIPANT NAMES: Stacked on the left side with collision detection
        if (betParticipants.length >= 2) {
          const leftX = x1 - 15; // Position to the left of the bet line
          const baseY = y - 20;   // Start above the bet line
          
          // Sort participants: winner first, then others
          const sortedParticipants = [...betParticipants].sort((a, b) => {
            if (a!.success_status === 'success') return -1;
            if (b!.success_status === 'success') return 1;
            return 0;
          });
          
          // Prepare all labels (participants + "vs") for collision detection
          const allLabelData: Array<{x: number, y: number, height: number, text: string, color: string, id: string, type: string, participant?: any}> = [];
          
          // Add participant labels
          sortedParticipants.forEach((participant, index) => {
            if (!participant) return;
            
            const participantName = participant.influencer?.name || 'Unknown';
            const statusSymbol = participant.success_status === 'success' ? ' ✓' : 
                                participant.success_status === 'miss' ? ' ✗' : '';
            
            allLabelData.push({
              x: leftX,
              y: baseY + (index * 25), // Start with more spacing
              height: 16,
              text: participantName + statusSymbol,
              color: getStatusColor(participant, true),
              id: `participant-${participant.id}`,
              type: 'participant',
              participant: participant
            });
            
            // Add "vs" label between participants (except after the last one)
            if (index < sortedParticipants.length - 1) {
              allLabelData.push({
                x: leftX,
                y: baseY + (index * 25) + 12, // Position between participants
                height: 12,
                text: 'vs',
                color: '#888888',
                id: `vs-${index}`,
                type: 'vs'
              });
            }
          });
          
          // Resolve collisions for all labels together
          const resolvedLabels = resolveCollisions(allLabelData);
          
          // Render all resolved labels
          resolvedLabels.forEach((labelInfo) => {
            const fontSize = labelInfo.type === 'vs' ? '10px' : '12px';
            const fontWeight = labelInfo.type === 'vs' ? '400' : '600';
            
            addLabelWithBackground(
              chartArea,
              labelInfo.x,
              labelInfo.y,
              labelInfo.text,
              labelInfo.color,
              'end',
              resetZoom,
              fontSize,
              fontWeight
            );
          });
        } else {
          // Single participant (fallback)
          const participantName = prediction.influencer?.name || 'Unknown';
          addLabelWithBackground(
            chartArea,
            x1 - 15,
            y - 8,
            participantName,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '12px',
            '600'
          );
        }

        // BET AMOUNT LABEL: Show on the right side  
        addLabelWithBackground(
          chartArea,
          x2 + 8,
          y - 8,
          `🎲 ${formatChartPrice(betPrice)}`,
          getStatusColor(prediction, true),
          'start',
          resetZoom
        );
        
        // Add status indicator for bet predictions (show overall bet status)
        const overallStatus: 'success' | 'miss' | 'updated' | 'pending' = 
          betParticipants.some(p => p?.success_status === 'success') ? 'success' :
          betParticipants.some(p => p?.success_status === 'miss') ? 'miss' :
          betParticipants.some(p => p?.success_status === 'updated') ? 'updated' : 'pending';
        
        const statusPrediction = { ...prediction, success_status: overallStatus };
        const statusText = getStatusText(statusPrediction);
        
        addLabelWithBackground(
          chartArea,
          x2 + 8,
          y + 10,
          statusText,
          getStatusColor(statusPrediction, true),
          'start',
          resetZoom,
          '10px',
          '500'
        );
      }
    });

    // ADD SUCCESS CROSSING POINTS: Show green dots where successful predictions were reached
    const successfulPredictions = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p && p.success_status === 'success');

    successfulPredictions.forEach(prediction => {
      if (!prediction) return;
      
      
      // Find where the price actually crossed the prediction target
      const crossingPoint = findCrossingPoint(processedData, prediction);
      
      if (crossingPoint) {
        const crossX = zoomedXScale(crossingPoint.time);
        const crossY = zoomedYScale(crossingPoint.value);
        
        
        // Large green success dot
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 6) // Larger than regular prediction dots
          .attr("fill", predictionStyles.colors.successSolid)
          .attr("stroke", "white")
          .attr("stroke-width", 2)
          .style("opacity", 1);
        
        // Add a subtle pulse ring around the success dot
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 8)
          .attr("fill", "none")
          .attr("stroke", predictionStyles.colors.successSolid)
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.3);
        
        // Success label showing when target was reached
        addLabelWithBackground(
          chartArea,
          crossX - 8,
          crossY - 12,
          `✓ ${crossingPoint.time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          predictionStyles.colors.successSolid,
          'end',
          undefined,
          '11px',
          '600'
        );
      } else {
      }
    });

  }, [chartData, predictions, activePredictions, isDarkMode, theme, resetZoom]);

  // Initialize zoom behavior and redraw chart
  useEffect(() => {
    if (!svgRef.current) return;

    // Create zoom behavior with Y-axis constraints
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 50]) // Allow zoom out to 50% and zoom in to 5000% for detailed analysis
      .translateExtent([[-800, -200], [Infinity, 800]]) // Allow more leftward panning to reach 2015 data
      .on('zoom', (event) => {
        const transform = event.transform;
        // Prevent horizontal panning to the left (going backwards in time)
        if (transform.x > 0) {
          transform.x = 0;
        }
        transformRef.current = transform;
        redrawChart(transform);
      });

    // Apply zoom to SVG
    const svg = d3.select(svgRef.current);
    svg.call(zoom);
    zoomRef.current = zoom;

    // Initial draw
    redrawChart(transformRef.current);

    // Cleanup
    return () => {
      svg.on('.zoom', null);
    };
  }, [redrawChart]);

  // Reset zoom when all predictions are cleared (reset button clicked)
  useEffect(() => {
    if (activePredictions.length === 0) {
      resetZoom();
    }
  }, [activePredictions.length, resetZoom]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height={280} // Reduced from 400px to match new ChartContainer height
      style={{ 
        background: 'transparent', 
        display: 'block',
        // Safari compatibility improvements
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        // Enable pinch-to-zoom and pan gestures
        touchAction: 'none', // Changed from 'pan-x pan-y' to 'none' to allow D3 zoom to handle all touch events
        // Prevent rubber band scrolling on iOS Safari
        overscrollBehavior: 'contain',
        // Cursor styles for desktop
        cursor: 'grab'
      }}
    />
  );
};

export default MobilePriceChart;